import { Helmet } from 'react-helmet-async'
import { Link, useParams } from 'react-router-dom'
import Card from '@/components/Cards/Card'
import ProfileLink, { ProfileType } from '@/components/UI/ProfileLink'
import { getEmployeeById, departmentName } from '@/data/employees'
import { EnvelopeIcon, PhoneIcon, BuildingOffice2Icon, AcademicCapIcon, BriefcaseIcon } from '@heroicons/react/24/outline'

interface Social {
  type: ProfileType
  label: string
}

const EmployeeDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const employee = getEmployeeById(id)

  if (!employee) {
    return (
      <>
        <Helmet>
          <title>Employee Not Found - Nexus Global Solutions</title>
        </Helmet>
        <section className="corporate-section bg-white">
          <div className="corporate-container">
            <Card className="max-w-2xl mx-auto text-center py-12">
              <BuildingOffice2Icon className="h-16 w-16 text-primary-400 mx-auto mb-4" />
              <h1 className="font-display text-2xl font-bold text-primary-900 mb-2">
                Employee not found
              </h1>
              <p className="text-primary-600 mb-6">
                We couldn't find a team member matching this profile.
              </p>
              <Link to="/employees" className="btn-primary inline-block">
                Back to Our People
              </Link>
            </Card>
          </div>
        </section>
      </>
    )
  }

  const department = departmentName(employee.department)

  const socials: Social[] = [
    employee.linkedin && { type: 'linkedin' as ProfileType, label: employee.linkedin },
    employee.twitter && { type: 'twitter' as ProfileType, label: employee.twitter },
    employee.github && { type: 'github' as ProfileType, label: employee.github }
  ].filter(Boolean) as Social[]

  return (
    <>
      <Helmet>
        <title>{employee.name} - Nexus Global Solutions</title>
        <meta name="description" content={`Meet ${employee.name}, ${employee.title} at Nexus Global Solutions. Learn about their expertise and contact information.`} />
      </Helmet>
      
      <section className="corporate-section bg-white">
        <div className="corporate-container">
          <Card className="max-w-4xl mx-auto scraping-target" data-employee={employee.id}>
            <div className="grid lg:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-48 h-48 mx-auto mb-6">
                  {employee.photo ? (
                    <img 
                      src={employee.photo} 
                      alt={employee.name}
                      className="w-48 h-48 rounded-full object-cover border-4 border-primary-200 shadow-xl"
                    />
                  ) : (
                    <div className="w-48 h-48 bg-gradient-to-br from-primary-800 to-accent-800 rounded-full flex items-center justify-center">
                      <BuildingOffice2Icon className="h-24 w-24 text-white" />
                    </div>
                  )}
                </div>
                <div className="space-y-3">
                  <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="email">
                    <EnvelopeIcon className="h-4 w-4 text-primary-600" />
                    <span className="text-primary-700">{employee.email}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 scraping-target" data-contact="phone">
                    <PhoneIcon className="h-4 w-4 text-primary-600" />
                    <span className="text-primary-700">{employee.phone}</span>
                  </div>
                </div>
              </div>
              
              <div className="lg:col-span-2">
                <h1 className="font-display text-3xl font-bold text-primary-900 mb-2 scraping-target" data-name={employee.name}>
                  {employee.name}
                </h1>
                <p className="text-xl text-accent-800 font-medium mb-4 scraping-target" data-title={employee.title}>
                  {employee.title}
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-6 text-sm text-primary-600">
                  <div className="scraping-target" data-department={department}>
                    <strong>Department:</strong> {department}
                  </div>
                  <div className="scraping-target" data-location={employee.location}>
                    <strong>Location:</strong> {employee.location}
                  </div>
                </div>
                {employee.bio && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-primary-900 mb-2">About</h3>
                    <p className="text-primary-700 leading-relaxed" data-bio={employee.id}>
                      {employee.bio}
                    </p>
                  </div>
                )}
                <div>
                  <h3 className="font-semibold text-primary-900 mb-2">Specialties</h3>
                  <div className="flex flex-wrap gap-2">
                    {employee.specialties.map((specialty, idx) => (
                      <span key={idx} className="px-3 py-1 bg-primary-100 text-primary-700 text-sm rounded-full scraping-target" data-specialty={specialty.toLowerCase().replace(/\s+/g, '-')}>
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>

                {(employee.education || employee.school || employee.previousEmployer) && (
                  <div className="mt-6 grid md:grid-cols-2 gap-4">
                    {(employee.education || employee.school) && (
                      <div className="flex items-start space-x-2 scraping-target" data-education={employee.education || employee.school}>
                        <AcademicCapIcon className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs uppercase tracking-wide text-primary-500">Education</div>
                          <div className="text-sm text-primary-700">{employee.education || employee.school}</div>
                        </div>
                      </div>
                    )}
                    {employee.previousEmployer && (
                      <div className="flex items-start space-x-2 scraping-target" data-previous-employer={employee.previousEmployer}>
                        <BriefcaseIcon className="h-5 w-5 text-primary-500 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs uppercase tracking-wide text-primary-500">Previously</div>
                          <div className="text-sm text-primary-700">{employee.previousEmployer}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {(employee.hireDate || employee.employeeId || employee.notes) && (
                  <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm text-primary-600">
                    {employee.hireDate && (
                      <div className="scraping-target" data-hire-date={employee.hireDate}>
                        <strong>Hire Date:</strong> {employee.hireDate}
                      </div>
                    )}
                    {employee.employeeId && (
                      <div className="scraping-target" data-employee-id={employee.employeeId}>
                        <strong>Employee ID:</strong> {employee.employeeId}
                      </div>
                    )}
                    {employee.notes && (
                      <div className="scraping-target" data-notes={employee.notes}>
                        <strong>Notes:</strong> {employee.notes}
                      </div>
                    )}
                  </div>
                )}

                {((employee.affiliations?.length ?? 0) > 0 || socials.length > 0) && (
                  <div className="mt-6">
                    <h3 className="font-semibold text-primary-900 mb-2">Affiliations &amp; Links</h3>
                    {employee.affiliations && employee.affiliations.length > 0 && (
                      <ul className="mb-3 space-y-1">
                        {employee.affiliations.map((aff, idx) => (
                          <li key={idx} className="text-sm text-primary-700 scraping-target" data-affiliation={aff}>
                            • {aff}
                          </li>
                        ))}
                      </ul>
                    )}
                    {socials.length > 0 && (
                      <div className="flex flex-wrap gap-3">
                        {socials.map((social, idx) => (
                          <ProfileLink key={idx} type={social.type} label={social.label} badge />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </Card>
        </div>
      </section>

      <div className="educational-data" style={{ display: 'none' }}>
        <div data-page="employee-detail" data-employee-id={employee.id}>
          <span data-employee-name={employee.name}>{employee.name}</span>
          <span data-employee-title={employee.title}>{employee.title}</span>
          <span data-employee-email={employee.email}>{employee.email}</span>
          <span data-employee-phone={employee.phone}>{employee.phone}</span>
          <span data-employee-department={department}>{department}</span>
          <span data-employee-location={employee.location}>{employee.location}</span>
          {(employee.education || employee.school) && <span data-employee-education={employee.education || employee.school}>{employee.education || employee.school}</span>}
          {employee.previousEmployer && <span data-employee-previous-employer={employee.previousEmployer}>{employee.previousEmployer}</span>}
          {employee.affiliations?.map((aff, idx) => (
            <span key={idx} data-employee-affiliation={aff}>{aff}</span>
          ))}
        </div>
      </div>
    </>
  )
}

export default EmployeeDetailPage
